import { ApiError, createRequest } from './http';
import { localizeDataResponse, localizeDimensions, localizeIndicator, localizeTerritories } from './data-locale';
import type {
  AuthMe,
  DashboardDataResponse,
  DashboardVisibility,
  DataBatchResponse,
  GeoTicket,
  DashboardDefinition,
  DashboardOut,
  DashboardScope,
  DashboardScopeOptions,
  DashboardSummary,
  DataQueryResponse,
  EmissCatalogRefresh,
  EmissCatalogSearch,
  EmissCatalogStatus,
  EmissDiagnostics,
  EmissFilterValueResponse,
  EmissInspection,
  EmissPlan,
  EmissRegisterPayload,
  EmissRegisterResponse,
  EmissStatus,
  SourceRequestLog,
  TerritoryRescopeResponse,
  Indicator,
  IndicatorDimension,
  IndicatorPeriods,
  IndicatorSection,
  Locale,
  Territory,
  WidgetQuery,
  WorldBankCatalogRefresh,
  WorldBankCatalogSearch,
  WorldBankCatalogStatus,
  WorldBankRegisterResponse,
} from './types';

// Клиент бэкенда: каталоги, данные, дашборды, внешние источники.
// Геометрия — в gis-api.ts.
const request = createRequest('/api/v1');

// Язык запросов к бэкенду — всегда русский: при locale=en бэкенд
// транслитерирует названия (см. data-locale.ts). Английские названия
// подставляются на фронтенде из name_en и словаря.
const BACKEND_LOCALE: Locale = 'ru';

// --- Каталоги при смене языка ------------------------------------------------
//
// Ответы каталогов одинаковы на любом языке (BACKEND_LOCALE), переводит их
// фронтенд. Поэтому последний ответ каждого адреса запоминается, и смена языка
// (withCachedCatalogs) переводит его заново без запроса. Обычные вызовы всегда
// идут на сервер.
const CATALOG_CACHE_LIMIT = 200;
const catalogCache = new Map<string, unknown>();
let preferCachedCatalogs = 0;

async function catalogRequest<T>(url: string): Promise<T> {
  if (preferCachedCatalogs > 0 && catalogCache.has(url)) return structuredClone(catalogCache.get(url)) as T;
  const result = await request<T>(url);
  catalogCache.delete(url);
  catalogCache.set(url, structuredClone(result));
  if (catalogCache.size > CATALOG_CACHE_LIMIT) catalogCache.delete(catalogCache.keys().next().value as string);
  return result;
}

/** Выполнить fn, беря каталоги из последних ответов, если они есть (смена языка). */
export async function withCachedCatalogs<T>(fn: () => Promise<T>): Promise<T> {
  preferCachedCatalogs += 1;
  try {
    return await fn();
  } finally {
    preferCachedCatalogs -= 1;
  }
}

// --- Пачки запросов данных (CODE_AUDIT 5.1) -------------------------------
//
// Рендер сетки запускает загрузку всех виджетов в одном такте, и раньше это
// были N отдельных POST /data/query. Здесь вызовы одного такта копятся и
// уходят одним POST /data/query/batch; каждый виджет получает свой результат
// или свою ошибку с тем же текстом, что дал бы одиночный запрос. Одиночный
// вызов идёт старым путём — для него ничего не меняется.

type DataQueryBody = Record<string, unknown>;
type PendingQuery = {
  body: DataQueryBody;
  resolve: (data: DataQueryResponse) => void;
  reject: (error: Error) => void;
};

/** Потолок пачки на сервере (max_batch_queries). */
const MAX_BATCH_QUERIES = 50;
let pendingQueries: PendingQuery[] = [];
let flushScheduled = false;

function enqueueDataQuery(body: DataQueryBody): Promise<DataQueryResponse> {
  return new Promise((resolve, reject) => {
    pendingQueries.push({ body, resolve, reject });
    if (!flushScheduled) {
      flushScheduled = true;
      window.setTimeout(flushDataQueries, 0);
    }
  });
}

function flushDataQueries(): void {
  flushScheduled = false;
  const queue = pendingQueries;
  pendingQueries = [];
  for (let start = 0; start < queue.length; start += MAX_BATCH_QUERIES) {
    void sendDataQueries(queue.slice(start, start + MAX_BATCH_QUERIES));
  }
}

async function sendDataQueries(batch: PendingQuery[]): Promise<void> {
  if (batch.length === 1) {
    const [only] = batch as [PendingQuery];
    try {
      only.resolve(await request<DataQueryResponse>('/data/query', { method: 'POST', body: JSON.stringify(only.body) }));
    } catch (error) {
      only.reject(error instanceof Error ? error : new Error(String(error)));
    }
    return;
  }
  try {
    const response = await request<DataBatchResponse>('/data/query/batch', {
      method: 'POST',
      body: JSON.stringify({ queries: batch.map((item) => item.body) }),
    });
    batch.forEach((item, index) => {
      const result = response.results[index];
      if (result?.status === 200 && result.data) item.resolve(result.data);
      else item.reject(new ApiError(result?.status ?? 500, result?.detail));
    });
  } catch (error) {
    const failure = error instanceof Error ? error : new Error(String(error));
    batch.forEach((item) => item.reject(failure));
  }
}

type DashboardCapabilityFields = Pick<
  DashboardSummary,
  'owner' | 'is_template' | 'is_demo' | 'favorite' | 'can_rename' | 'can_delete' | 'can_copy' | 'visibility' | 'can_edit' | 'can_change_visibility' | 'is_owner'
>;
type RawDashboard<T> = Omit<T, keyof DashboardCapabilityFields> & Partial<DashboardCapabilityFields>;

// Бэкенд до модели доступа не знал visibility/can_edit/is_owner: умолчания
// повторяют его прежнее поведение (всё «своё», шаблоны только для чтения).
function normalizeDashboardCapabilities<T extends DashboardSummary | DashboardOut>(dashboard: RawDashboard<T>): T {
  const isTemplate = dashboard.is_template === true;
  const isDemo = dashboard.is_demo === true;
  const canRename = dashboard.can_rename ?? !isTemplate;
  return {
    ...dashboard,
    owner: dashboard.owner ?? '',
    is_template: isTemplate,
    is_demo: isDemo,
    favorite: dashboard.favorite === true,
    can_rename: canRename,
    can_delete: dashboard.can_delete ?? !isTemplate,
    can_copy: dashboard.can_copy ?? true,
    visibility: dashboard.visibility ?? (isTemplate || isDemo ? 'public' : 'private'),
    can_edit: dashboard.can_edit ?? canRename,
    can_change_visibility: dashboard.can_change_visibility === true,
    is_owner: dashboard.is_owner ?? !isTemplate,
  } as T;
}

// --- Данные сохранённого дашборда (анонимный просмотр) ---------------------
//
// Аноним при AUTH_MODE=required не может запрашивать /data/query: бэкенд сам
// исполняет запросы виджетов из сохранённого определения (POST
// /dashboards/{id}/data). Вызовы одного такта копятся и уходят одним запросом
// со списком widget_ids, как пачка /data/query/batch.

type PendingWidget = {
  widgetId: string;
  resolve: (data: DataQueryResponse) => void;
  reject: (error: Error) => void;
};

const pendingWidgets = new Map<string, PendingWidget[]>();

function enqueueDashboardWidget(dashboardId: string, widgetId: string, scopeTerritoryId: string | null): Promise<DataQueryResponse> {
  // Пачка — на дашборд и территорию сквозного фильтра: у разных территорий разные запросы.
  const key = `${dashboardId}
${scopeTerritoryId ?? ''}`;
  return new Promise((resolve, reject) => {
    let queue = pendingWidgets.get(key);
    if (!queue) {
      queue = [];
      pendingWidgets.set(key, queue);
      window.setTimeout(() => {
        const batch = pendingWidgets.get(key) ?? [];
        pendingWidgets.delete(key);
        void sendDashboardWidgets(dashboardId, scopeTerritoryId, batch);
      }, 0);
    }
    queue.push({ widgetId, resolve, reject });
  });
}

async function sendDashboardWidgets(dashboardId: string, scopeTerritoryId: string | null, batch: PendingWidget[]): Promise<void> {
  try {
    const response = await request<DashboardDataResponse>(`/dashboards/${encodeURIComponent(dashboardId)}/data`, {
      method: 'POST',
      body: JSON.stringify({
        widget_ids: [...new Set(batch.map((item) => item.widgetId))],
        ...(scopeTerritoryId ? { scope_territory_id: scopeTerritoryId } : {}),
        locale: BACKEND_LOCALE,
      }),
    });
    const byWidget = new Map(response.results.map((result) => [result.widget_id, result]));
    batch.forEach((item) => {
      const result = byWidget.get(item.widgetId);
      if (result?.status === 200 && result.data) item.resolve(result.data);
      else item.reject(new ApiError(result?.status ?? 404, result?.detail ?? 'Widget not found in the saved dashboard'));
    });
  } catch (error) {
    const failure = error instanceof Error ? error : new Error(String(error));
    batch.forEach((item) => item.reject(failure));
  }
}

export function localizeWithComparison(response: DataQueryResponse, locale: Locale): DataQueryResponse {
  const localized = localizeDataResponse(response, locale);
  const comparison = (localized.meta.source_details as { comparison?: { responses?: DataQueryResponse[] } } | undefined)?.comparison;
  if (!comparison?.responses?.length) return localized;
  return {
    ...localized,
    meta: {
      ...localized.meta,
      source_details: {
        ...(localized.meta.source_details ?? {}),
        comparison: { ...comparison, responses: comparison.responses.map((item) => localizeDataResponse(item, locale)) },
      },
    },
  };
}

export interface TerritoryRequest {
  sourceType?: string;
  indicatorId?: string;
  level?: string;
  parentId?: string;
  federalDistrictId?: string;
  regionId?: string;
  territoryId?: string;
  year?: number;
  search?: string;
  limit?: number;
}

export interface IndicatorRequest {
  sourceType?: string;
  q?: string;
  sectionId?: string;
}

function queryString(values: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.set(key, String(value));
  });
  const encoded = params.toString();
  return encoded ? `?${encoded}` : '';
}

export const api = {
  indicators: async (activeOnly = true, locale: Locale = 'ru', options: IndicatorRequest = {}): Promise<Indicator[]> =>
    (await catalogRequest<Indicator[]>(`/catalog/indicators${queryString({
      active_only: activeOnly ? 'true' : 'false',
      source_type: options.sourceType,
      q: options.q,
      section_id: options.sectionId,
      locale: BACKEND_LOCALE,
    })}`)).map((indicator) => localizeIndicator(indicator, locale)),
  indicatorSections: (sourceType: string, _locale: Locale = 'ru'): Promise<IndicatorSection[]> =>
    catalogRequest(`/catalog/indicator-sections${queryString({ source_type: sourceType, locale: BACKEND_LOCALE })}`),
  dimensions: async (indicatorId: string, locale: Locale = 'ru'): Promise<IndicatorDimension[]> =>
    localizeDimensions(
      await catalogRequest<IndicatorDimension[]>(`/catalog/indicators/${encodeURIComponent(indicatorId)}/dimensions${queryString({ locale: BACKEND_LOCALE })}`),
      locale,
    ),
  territories: async (options: TerritoryRequest = {}, locale: Locale = 'ru'): Promise<Territory[]> =>
    localizeTerritories(await catalogRequest<Territory[]>(`/catalog/territories${queryString({
      source_type: options.sourceType,
      indicator_id: options.indicatorId,
      level: options.level,
      parent_id: options.parentId,
      federal_district_id: options.federalDistrictId,
      region_id: options.regionId,
      territory_id: options.territoryId,
      year: options.year,
      search: options.search,
      limit: options.limit,
      locale: BACKEND_LOCALE,
    })}`), locale),
  periods: (indicatorId: string, _locale: Locale = 'ru'): Promise<IndicatorPeriods> =>
    catalogRequest(`/catalog/indicators/${encodeURIComponent(indicatorId)}/periods${queryString({ locale: BACKEND_LOCALE })}`),
  queryData: async (query: WidgetQuery, forceRefresh = false, locale: Locale = 'ru'): Promise<DataQueryResponse> =>
    localizeDataResponse(await enqueueDataQuery({
      indicator_id: query.indicatorId,
      territory_ids: query.territoryIds,
      years: query.years,
      months: query.months,
      dimensions: query.dimensions,
      force_refresh: forceRefresh,
      locale: BACKEND_LOCALE,
    }), locale),
  planEmissQuery: (localId: string, query: WidgetQuery, forceRefresh = false): Promise<EmissPlan> =>
    request(`/emiss/indicators/${encodeURIComponent(localId)}/plan`, {
      method: 'POST',
      body: JSON.stringify({
        territory_ids: query.territoryIds,
        years: query.years,
        months: query.months,
        dimensions: query.dimensions,
        force_refresh: forceRefresh,
      }),
    }),

  emissStatus: (): Promise<EmissStatus> => request('/emiss/status'),
  emissCatalogStatus: (): Promise<EmissCatalogStatus> => request('/emiss/catalog/status'),
  refreshEmissCatalog: (): Promise<EmissCatalogRefresh> =>
    request('/emiss/catalog/refresh', { method: 'POST' }),
  searchEmissCatalog: (
    q = '',
    includeExcluded = false,
    limit = 25,
    offset = 0,
    locale: Locale = 'ru',
    sectionId = '',
  ): Promise<EmissCatalogSearch> =>
    request(`/emiss/catalog/search${queryString({
      q,
      include_excluded: includeExcluded ? 'true' : 'false',
      section_id: sectionId,
      limit,
      offset,
      // Английское название ЕМИСС бэкенд берёт из title_en или транслитерирует.
      locale: BACKEND_LOCALE,
    })}`),
  worldBankCatalogStatus: (): Promise<WorldBankCatalogStatus> => request('/worldbank/catalog/status'),
  refreshWorldBankCatalog: (): Promise<WorldBankCatalogRefresh> =>
    request('/worldbank/catalog/refresh', { method: 'POST' }),
  searchWorldBankCatalog: (
    q = '',
    limit = 25,
    offset = 0,
    locale: Locale = 'ru',
    sectionId = '',
  ): Promise<WorldBankCatalogSearch> =>
    request(`/worldbank/catalog/search${queryString({ q, section_id: sectionId, limit, offset, locale })}`),
  registerWorldBank: (indicatorId: string): Promise<WorldBankRegisterResponse> =>
    request('/worldbank/register', {
      method: 'POST',
      body: JSON.stringify({ indicator_id: indicatorId }),
    }),
  emissDiagnostics: (indicatorId: string, forceRefresh = true): Promise<EmissDiagnostics> =>
    request('/emiss/diagnostics', {
      method: 'POST',
      body: JSON.stringify({ indicator_id: indicatorId, force_refresh: forceRefresh }),
    }),
  inspectEmiss: (
    indicatorId: string,
    options: {
      forceRefresh?: boolean;
      fieldMap?: Record<string, string>;
      fixedFilters?: Record<string, string[]>;
      dimensionDefaults?: Record<string, string[]>;
    } = {},
  ): Promise<EmissInspection> =>
    request('/emiss/inspect', {
      method: 'POST',
      body: JSON.stringify({
        indicator_id: indicatorId,
        force_refresh: options.forceRefresh ?? false,
        field_map: options.fieldMap ?? {},
        fixed_filters: options.fixedFilters ?? {},
        dimension_defaults: options.dimensionDefaults ?? {},
      }),
    }),
  registerEmiss: (payload: EmissRegisterPayload): Promise<EmissRegisterResponse> =>
    request('/emiss/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  emissFilterValues: (
    indicatorId: string,
    fieldId: string,
    search = '',
    limit = 100,
    offset = 0,
  ): Promise<EmissFilterValueResponse> =>
    request(`/emiss/indicators/${encodeURIComponent(indicatorId)}/filters/${encodeURIComponent(fieldId)}/values${queryString({ search, limit, offset })}`),
  emissRequestLogs: (limit = 30): Promise<SourceRequestLog[]> =>
    request(`/emiss/request-logs${queryString({ limit })}`),
  dashboards: async (): Promise<DashboardSummary[]> =>
    (await request<RawDashboard<DashboardSummary>[]>('/dashboards')).map(normalizeDashboardCapabilities),
  dashboard: async (id: string): Promise<DashboardOut> =>
    normalizeDashboardCapabilities(await request<RawDashboard<DashboardOut>>(`/dashboards/${encodeURIComponent(id)}`)),
  createDashboard: (name: string, definition: DashboardDefinition, visibility?: DashboardVisibility): Promise<DashboardOut> =>
    request<RawDashboard<DashboardOut>>('/dashboards', {
      method: 'POST',
      body: JSON.stringify(visibility ? { name, definition, visibility } : { name, definition }),
    }).then(normalizeDashboardCapabilities),
  copyDashboard: (id: string, name?: string): Promise<DashboardOut> =>
    request<RawDashboard<DashboardOut>>(`/dashboards/${encodeURIComponent(id)}/copy`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }).then(normalizeDashboardCapabilities),
  updateDashboardMetadata: (id: string, metadata: { name?: string; favorite?: boolean; visibility?: DashboardVisibility; owner?: string }): Promise<DashboardOut> =>
    request<RawDashboard<DashboardOut>>(`/dashboards/${encodeURIComponent(id)}/metadata`, {
      method: 'PATCH',
      body: JSON.stringify(metadata),
    }).then(normalizeDashboardCapabilities),
  updateDashboard: (id: string, name: string, definition: DashboardDefinition): Promise<DashboardOut> =>
    request<RawDashboard<DashboardOut>>(`/dashboards/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ name, definition }),
    }).then(normalizeDashboardCapabilities),
  deleteDashboard: (id: string): Promise<void> =>
    request(`/dashboards/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /**
   * Данные виджета сохранённого дашборда — запрос берёт бэкенд из определения;
   * scopeTerritoryId — территория сквозного фильтра, территории переписывает бэкенд.
   */
  dashboardWidgetData: async (
    dashboardId: string,
    widgetId: string,
    locale: Locale = 'ru',
    scopeTerritoryId: string | null = null,
  ): Promise<DataQueryResponse> =>
    localizeWithComparison(await enqueueDashboardWidget(dashboardId, widgetId, scopeTerritoryId), locale),
  /** Варианты сквозного фильтра сохранённого дашборда (открыт анониму). */
  dashboardScopeOptions: async (dashboardId: string, locale: Locale = 'ru'): Promise<DashboardScopeOptions> => {
    const options = await request<DashboardScopeOptions>(
      `/dashboards/${encodeURIComponent(dashboardId)}/scope-options${queryString({ locale: BACKEND_LOCALE })}`,
    );
    return { ...options, territories: localizeTerritories(options.territories, locale) };
  },
  /** Территории запросов виджетов после сквозного фильтра — для несохранённого определения. */
  rescopeTerritories: (
    scope: DashboardScope,
    targetTerritoryId: string,
    items: Array<{ indicatorId: string; territoryIds: string[] }>,
  ): Promise<TerritoryRescopeResponse> =>
    request('/data/rescope', {
      method: 'POST',
      body: JSON.stringify({
        scope,
        target_territory_id: targetTerritoryId,
        items: items.map((item) => ({ indicator_id: item.indicatorId, territory_ids: item.territoryIds })),
      }),
    }),
  authMe: (): Promise<AuthMe> => request('/auth/me'),
  geoTicket: (dashboardId?: string): Promise<GeoTicket> =>
    request('/auth/geo-ticket', {
      method: 'POST',
      body: JSON.stringify(dashboardId ? { dashboard_id: dashboardId } : {}),
    }),
};
